import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { StudioPage, Panel, SectionLabel, Metric, Badge, DataTable, Drawer, Loading, cx } from "./StudioShared";
import ActivityTimeline from "@/components/ActivityTimeline";
import { Upload, FileText, CheckCircle2, XCircle, Trash2, Download, Layers, GraduationCap, Plus } from "lucide-react";

const inp = "w-full bg-navy border border-white/10 px-3 py-2 text-sm text-white rounded-sm focus:border-gold focus:outline-none";
const VERDICT_TONE = { approved: "green", rejected: "red", pending: "amber" };
const EXT_TONE = { pdf: "red", docx: "gold", csv: "green" };

export default function TrainingCenter() {
  const [tab, setTab] = useState("docs");
  return (
    <StudioPage title="Training Center" testid="training-center-page"
      question="Teach AIPOS — upload knowledge, extract & review it, group it into training sets, and assign it across the platform.">
      <Panel className="p-1.5 mb-6 flex gap-1" data-testid="train-tabs">
        {[["docs", "Documents", FileText], ["sets", "Training Sets", Layers]].map(([k, l, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`train-tab-${k}`}
            className={cx("flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-sm transition-colors", tab === k ? "bg-gold text-navy" : "text-slate-400 hover:text-white hover:bg-white/5")}>
            <Icon size={14} /> {l}
          </button>
        ))}
      </Panel>
      {tab === "docs" ? <Documents /> : <Sets />}
    </StudioPage>
  );
}

function Documents() {
  const [items, setItems] = useState(null);
  const [meta, setMeta] = useState(null);
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [opts, setOpts] = useState({ departments: [], strategists: [], missions: [] });
  const [form, setForm] = useState({ file: null, title: "", category: "General", tags: "" });

  const load = useCallback(async () => {
    const p = new URLSearchParams();
    if (category) p.set("category", category);
    if (q) p.set("q", q);
    const [list, m] = await Promise.all([api.get(`/admin/training/docs?${p}`), api.get("/admin/training/docs/meta")]);
    setItems(list.data); setMeta(m.data);
  }, [category, q]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    Promise.all([api.get("/admin/departments"), api.get("/admin/strategists"), api.get("/admin/missions")])
      .then(([d, s, mi]) => setOpts({ departments: d.data.map((x) => ({ id: x.name, label: x.name })), strategists: s.data.map((x) => ({ id: x.id, label: x.name })), missions: mi.data.map((x) => ({ id: x.id, label: x.name })) })).catch(() => {});
  }, []);

  const upload = async () => {
    if (!form.file) { toast.error("Choose a file"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", form.file); fd.append("title", form.title); fd.append("category", form.category); fd.append("tags", form.tags);
    try { const r = await api.post("/admin/training/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded "${r.data.title}" (${r.data.text_chars} chars extracted).`); setForm({ file: null, title: "", category: "General", tags: "" }); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const del = async (r) => { if (!window.confirm(`Delete "${r.title}"?`)) return; try { await api.delete(`/admin/training/docs/${r.id}`); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const download = async (r) => {
    try { const res = await api.get(`/admin/training/docs/${r.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = r.original_filename; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!items || !meta) return <Loading label="Loading training library…" />;
  const c = meta.counts;
  const columns = [
    { key: "title", label: "Document", render: (r) => (<div className="min-w-0"><div className="text-white truncate">{r.title}</div><div className="text-slate-500 text-xs truncate">{r.original_filename}</div></div>) },
    { key: "category", label: "Category" },
    { key: "ext", label: "Type", render: (r) => <Badge tone={EXT_TONE[r.ext] || "slate"}>{r.ext}</Badge> },
    { key: "extraction_status", label: "Extract", render: (r) => <Badge tone={r.extraction_status === "ok" ? "green" : "red"}>{r.extraction_status}</Badge> },
    { key: "text_chars", label: "Chars", render: (r) => (r.text_chars || 0).toLocaleString() },
    { key: "validation", label: "Review", render: (r) => <Badge tone={VERDICT_TONE[r.validation?.verdict]}>{r.validation?.verdict}</Badge> },
    { key: "actions", label: "", render: (r) => (
      <div className="flex justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
        <button onClick={() => download(r)} data-testid={`train-download-${r.id}`} title="Download" className="p-1.5 border border-white/10 text-slate-300 hover:border-gold/50 rounded-sm"><Download size={13} /></button>
        <button onClick={() => del(r)} data-testid={`train-del-${r.id}`} title="Delete" className="p-1.5 border border-red-500/30 text-red-300 hover:bg-red-500/10 rounded-sm"><Trash2 size={13} /></button>
      </div>) },
  ];

  return (
    <div data-testid="train-documents">
      <Panel className="p-5 mb-5" data-testid="train-upload">
        <SectionLabel className="mb-3 flex items-center gap-1.5"><Upload size={13} className="text-gold" /> Upload knowledge</SectionLabel>
        <div className="grid md:grid-cols-4 gap-3">
          <input type="file" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null, title: form.title || (e.target.files?.[0]?.name || "") })} data-testid="train-file" className={cx(inp, "file:text-gold file:bg-transparent file:border-0")} />
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title" data-testid="train-title" className={inp} />
          <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Category" data-testid="train-category" className={inp} />
          <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="Tags (comma)" data-testid="train-tags" className={inp} />
        </div>
        <button onClick={upload} disabled={busy || !form.file} data-testid="train-upload-btn" className="mt-3 flex items-center gap-1.5 bg-gold text-navy px-4 py-2 text-sm font-semibold rounded-sm hover:bg-gold-light disabled:opacity-50"><Upload size={14} /> {busy ? "Uploading…" : "Upload & Extract"}</button>
        <p className="text-xs text-slate-500 mt-2">Supported: {meta.supported_ext.join(", ")}. Vector/semantic indexing is future — text is extracted & stored now.</p>
      </Panel>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
        <Metric label="Documents" value={c.total} icon={GraduationCap} testid="train-count-total" />
        <Metric label="Active" value={c.active} testid="train-count-active" />
        <Metric label="Reviewed" value={c.reviewed} testid="train-count-reviewed" />
        <Metric label="Pending Review" value={c.pending} testid="train-count-pending" />
      </div>
      <Panel className="p-3 mb-5 flex flex-wrap gap-2">
        <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="train-cat-filter" className={cx(inp, "w-auto")}><option value="">All categories</option>{meta.categories.map((x) => <option key={x} value={x}>{x}</option>)}</select>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search documents & content…" data-testid="train-search" className={cx(inp, "flex-1 min-w-[160px]")} />
      </Panel>
      <DataTable testid="train-table" columns={columns} rows={items} onRowClick={(r) => setViewing(r)} rowTestid={(r) => `train-row-${r.id}`} empty="No documents yet. Upload knowledge to teach AIPOS." />
      {viewing && <DocDrawer docId={viewing.id} opts={opts} categories={meta.categories} onClose={() => setViewing(null)} onChange={load} />}
    </div>
  );
}

function DocDrawer({ docId, opts, categories, onClose, onChange }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/admin/training/docs/${docId}`).then((r) => setD(r.data)), [docId]);
  useEffect(() => { load(); }, [load]);
  const validate = async (verdict) => { setBusy(true); try { await api.post(`/admin/training/docs/${docId}/validate`, { verdict }); toast.success(`Marked ${verdict}.`); load(); onChange(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); } };
  const saveAssign = async (kind, ids) => { const assignments = { ...d.assignments, [kind]: ids }; setD({ ...d, assignments }); try { await api.patch(`/admin/training/docs/${docId}`, { assignments }); } catch (e) { toast.error(formatApiError(e)); } };
  if (!d) return <Drawer open onClose={onClose} testid="train-drawer" title="Document"><Loading /></Drawer>;
  return (
    <Drawer open onClose={onClose} testid="train-drawer" title={d.title} subtitle={`${d.ext.toUpperCase()} · ${(d.text || "").length.toLocaleString()} chars · v${d.version}`}>
      <div className="flex flex-wrap gap-2 items-center">
        <Badge tone={VERDICT_TONE[d.validation?.verdict]}>{d.validation?.verdict}</Badge>
        <button onClick={() => validate("approved")} disabled={busy} data-testid="train-approve" className="flex items-center gap-1 text-xs border border-emerald-500/40 text-emerald-300 px-2 py-1 rounded-sm hover:bg-emerald-500/10"><CheckCircle2 size={12} /> Approve</button>
        <button onClick={() => validate("rejected")} disabled={busy} data-testid="train-reject" className="flex items-center gap-1 text-xs border border-red-500/40 text-red-300 px-2 py-1 rounded-sm hover:bg-red-500/10"><XCircle size={12} /> Reject</button>
      </div>
      {["departments", "strategists", "missions"].map((k) => (
        <div key={k}><SectionLabel className="mb-1 capitalize">{k}</SectionLabel>
          <select multiple value={d.assignments?.[k] || []} onChange={(e) => saveAssign(k, Array.from(e.target.selectedOptions).map((o) => o.value))} data-testid={`train-assign-${k}`} className={cx(inp, "h-24")}>
            {opts[k].map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        </div>
      ))}
      <div><SectionLabel className="mb-1">Extracted text</SectionLabel>
        <pre className="whitespace-pre-wrap text-xs text-slate-400 bg-navy border border-white/10 rounded-sm p-3 max-h-72 overflow-auto" data-testid="train-text">{d.text || "(no text extracted)"}</pre>
      </div>
      <div className="pt-1"><ActivityTimeline title="Document Activity" mode="entity" params={{ entity_id: docId }} limit={20} testid="train-activity-timeline" emptyText="No document activity recorded yet." /></div>
    </Drawer>
  );
}

function Sets() {
  const [sets, setSets] = useState(null);
  const [docs, setDocs] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", doc_ids: [] });
  const load = useCallback(() => api.get("/admin/training/sets").then((r) => setSets(r.data)), []);
  useEffect(() => { load(); api.get("/admin/training/docs").then((r) => setDocs(r.data)); }, [load]);
  const create = async () => { try { await api.post("/admin/training/sets", form); toast.success("Training set created."); setCreating(false); setForm({ name: "", description: "", doc_ids: [] }); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const del = async (s) => { if (!window.confirm(`Delete set "${s.name}"?`)) return; try { await api.delete(`/admin/training/sets/${s.id}`); load(); } catch (e) { toast.error(formatApiError(e)); } };
  if (!sets) return <Loading />;
  return (
    <div data-testid="train-sets">
      <div className="flex justify-end mb-4"><button onClick={() => setCreating(true)} data-testid="train-new-set" className="flex items-center gap-1.5 bg-gold text-navy px-4 py-2 text-sm font-semibold rounded-sm hover:bg-gold-light"><Plus size={15} /> New Training Set</button></div>
      {sets.length === 0 ? <div className="text-slate-500 text-sm py-8 text-center">No training sets yet.</div> : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sets.map((s) => (
            <Panel key={s.id} className="p-4" data-testid={`train-set-${s.id}`}>
              <div className="flex justify-between items-start"><div><div className="text-white font-medium">{s.name}</div><div className="text-slate-500 text-xs mt-0.5">{s.description}</div></div>
                <button onClick={() => del(s)} className="text-slate-500 hover:text-red-400" data-testid={`train-set-del-${s.id}`}><Trash2 size={14} /></button></div>
              <div className="mt-3"><Badge tone="gold">{s.doc_count} documents</Badge></div>
            </Panel>
          ))}
        </div>
      )}
      {creating && (
        <Drawer open onClose={() => setCreating(false)} testid="train-set-drawer" title="New Training Set" subtitle="Group documents for reuse">
          <div><SectionLabel className="mb-1">Name</SectionLabel><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="train-set-name" className={inp} /></div>
          <div><SectionLabel className="mb-1">Description</SectionLabel><textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="train-set-desc" className={inp} /></div>
          <div><SectionLabel className="mb-1">Documents</SectionLabel>
            <select multiple value={form.doc_ids} onChange={(e) => setForm({ ...form, doc_ids: Array.from(e.target.selectedOptions).map((o) => o.value) })} data-testid="train-set-docs" className={cx(inp, "h-40")}>
              {docs.map((x) => <option key={x.id} value={x.id}>{x.title}</option>)}
            </select>
          </div>
          <button onClick={create} disabled={!form.name} data-testid="train-set-save" className="bg-gold text-navy px-4 py-2.5 text-sm font-semibold rounded-sm hover:bg-gold-light disabled:opacity-50">Create set</button>
        </Drawer>
      )}
    </div>
  );
}
